import {
  Box,
  LightModeSemanticColors,
  TableCell,
  TableRow,
  fontSizes,
} from '@enechain/ecloud-designsystem'

/**
 * 空 / loading 行の下限高さ。 design `tr.ecl-empty-state-row > td`
 * (`components.css` 「2026-07-23 デザイナー調整: 旧 200px はコンパクト化。下限」)。
 */
export const TABLE_MESSAGE_ROW_MIN_HEIGHT = '88px'

/** 空 / loading 行の td の style。 `TableMessageRow` と、 生 table の空行 (`TradeTable`) が共有する。 */
export const TABLE_MESSAGE_CELL_STYLE: React.CSSProperties = {
  padding: 0,
  height: '100%',
  minHeight: TABLE_MESSAGE_ROW_MIN_HEIGHT,
  verticalAlign: 'middle',
  borderBottom: 'none',
}

/**
 * 空 / loading 行の内側の箱の style (色は呼び出し側が渡す)。 `TABLE_MESSAGE_CELL_STYLE` と対。
 * 幅の意味は `TableMessageRow` の JSDoc 末尾を参照。
 */
export const TABLE_MESSAGE_BOX_STYLE: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'min(100%, 100cqi)',
  minHeight: TABLE_MESSAGE_ROW_MIN_HEIGHT,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  fontSize: fontSizes.md.value,
}

type Props = {
  /** 表の全列数 (= colSpan)。 */
  colSpan: number
  /** 表示する 1 行メッセージ (i18n 済みの文言を渡す)。 */
  message: string
  /** 既定は mid-emphasis。 error 等で色を変えたい時だけ渡す。 */
  color?: string
}

/**
 * 表の「行が 0 件 / 読込中 / エラー」を示す 1 行メッセージ行。
 *
 * design `tr.ecl-empty-state-row` (`design/ui-feasibility/components/table.md` §3、
 * DS `DataTable` の `emptyComponent` 相当) の React 版。 **メッセージは表の外ではなく
 * ヘッダー直下の行として出す** — 表の外に置くと列見出しだけが宙に浮き、
 * 「読み込み前」なのか「0 件」なのかが列の文脈から切り離される。
 *
 * - td   : padding 0 / min-height 88px / vertical-align middle / border-bottom なし
 * - inner: `position: sticky` + `left: 0` + `100cqi` で、 横スクロールしても
 *          「見えている幅」の中央に文言を保つ (全列幅の中央だと画面外へ逃げる)。
 * - 高さ : wrap が確定高さを持つ時はそこいっぱいに広げて中央、 持たない時は
 *          `min-height: 88px` に落ちる (`TableRoot` 側の `height: 100%` と対で効く)。
 * - 文字 : design 正はテーブルセル 14px (`design/css/layout.css` の body 14px を TD が
 *          継承、 design token `--font-size-md` も 14px)。 ecloud 0.14.2 の `fontSizes`
 *          は eclair と 1:1 (xs=10 / sm=12 / md=14 / lg=16px、 実測で確認済み) なので
 *          md がそのまま 14px に対応する。 sm=12px は誤り (計測で検出)。
 *
 * **前提**: スクロールする器 (`overflow-x: auto` を持つ wrap) に
 * `container-type: inline-size` が要る。 無いと `100cqi` がこの器を参照できず、
 * 横スクロールする狭い表示幅で文言が可視領域の中央から外れる。 block 軸は含めない
 * (`container-type: size` は過去に wrap を 0px へ潰す事故を起こしている)。
 *
 * 幅は `min(100%, 100cqi)` (= 表の幅と見えている幅の狭い方)。 器が `container-type` を
 * 持たない呼び出し側 (器を持たない共通の表 `ExecutionAreaSummaryTable` 等) では `100cqi` が
 * 画面幅に落ち、 文言の箱が画面幅まで伸びて横スクロールを生むため。 `container-type` を
 * 持つ器では、 表が器より広い時は見えている幅・狭い時は表の幅で、 どちらも見えている範囲の中央になる。
 */
export const TableMessageRow = ({
  colSpan,
  message,
  color = LightModeSemanticColors.Text.midEmphasis.value,
}: Props): React.ReactElement => (
  <TableRow style={{ height: '100%' }}>
    <TableCell colSpan={colSpan} style={TABLE_MESSAGE_CELL_STYLE}>
      <Box style={{ ...TABLE_MESSAGE_BOX_STYLE, color }}>{message}</Box>
    </TableCell>
  </TableRow>
)
